import { FormComponentFactory } from "../FormComponentFactory";
import { Component, ComponentConfig } from "../Component";
import { DataSetSelector } from "datasheets/components/selectors/DataSetSelector";
import { Fragment, useMemo } from "react";
import { useAtomValue } from "jotai";
import { dataCacheAtom } from "datasheets/model/atoms/WorkbookAtoms";
import {
  useComponentConfigAtom,
  useComponentCustomOrEditConfigAtom,
  useOutputWire,
} from "../state/ComponentAtoms";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { ColumnSelector } from "datasheets/components/selectors/ColumnSelector";

interface SelectComponent extends ComponentConfig {
  datasetId?: string;
  propertyKey?: string;
}
export function registerSelectComponent() {
  FormComponentFactory.addComponent("Select", {
    config: () => {
      return {
        connections: {
          inputs: [],
          outputs: [],
          inputConnectors: {},
          outputConnectors: {},
        },
      };
    },
    component: (config: Component) => {
      return <SelectDisplay config={config} />;
    },
    editor: (config: Component) => {
      return <SelectEditor componentId={config.id} />;
    },
    fixedOutputs: () => {
      return ["selected"];
    },
  });
}

function SelectDisplay({ config }: { config: Component }) {
  const cache = useAtomValue(dataCacheAtom);
  const [vanillaComponentConfig] = useComponentCustomOrEditConfigAtom(
    config.id
  );
  const componentConfig = vanillaComponentConfig as SelectComponent;
  /// Set up handlers for output wiring
  const selectedValueWireKey =
    "connections" in componentConfig &&
    "outputConnectors" in componentConfig.connections
      ? componentConfig.connections.outputConnectors["selected"]
      : undefined;
  const setWireValue = useOutputWire(selectedValueWireKey);
  // Check if we have enough data to display component
  const datasetId = componentConfig.datasetId;
  const propertyKey = componentConfig.propertyKey;
  if (!(!!datasetId && !!propertyKey)) {
    return <div>Data not found</div>;
  }
  const handleSelect = (ev: SelectChangeEvent) => {
    setWireValue(ev.target.value);
  };
  const data = cache[datasetId];
  let values: string[] = [];
  values = Object.values(data).map((row) => row[propertyKey]);
  return (
    <Select key={"Display-" + config.id} onChange={handleSelect}>
      {values.map((label) => {
        return (
          <MenuItem key={label} value={label}>
            {label}
          </MenuItem>
        );
      })}
    </Select>
  );
}

function SelectEditor({ componentId }: { componentId: string }) {
  const [config, setConfig] = useComponentConfigAtom(componentId);
  const componentConfig = config.editConfig as SelectComponent;
  const datasetId = componentConfig.datasetId;
  const setDatasetId = (newDatasetId?: string) => {
    setConfig((config: Component) => {
      return {
        ...config,
        ...{
          editConfig: { ...config.editConfig, ...{ datasetId: newDatasetId } },
        },
      };
    });
  };
  const propertyKey = useMemo(
    () => componentConfig.propertyKey,
    [componentConfig.propertyKey]
  );
  const setPropertyKey = (newPropertyKey?: string) => {
    setConfig((config: Component) => {
      return {
        ...config,
        ...{
          editConfig: {
            ...config.editConfig,
            ...{ propertyKey: newPropertyKey },
          },
        },
      };
    });
  };
  return (
    <Fragment key={"Editor-" + config.id}>
      <div style={{ fontSize: ".5em" }}>{config.id}</div>
      <div>
        <DataSetSelector
          onSelect={setDatasetId}
          title="Dataset"
          selection={datasetId}
        />
      </div>
      <div>
        <ColumnSelector
          title="Property Key"
          datasetId={datasetId}
          selection={propertyKey}
          onChange={(value: string) => setPropertyKey(value)}
          isSingleSelection={true}
        />
      </div>
    </Fragment>
  );
}
